import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, Paper, Stack, Grid, CircularProgress, Card, 
  MenuItem, Chip, IconButton, TableContainer
} from '@mui/material';
import {
  collection, query, where, getDocs, addDoc, updateDoc, 
  doc, deleteDoc, getDoc, writeBatch, orderBy
} from 'firebase/firestore';
import { auth, db } from '../../../firebase';
import { toast } from 'react-toastify';
import TablePagination from '@mui/material/TablePagination';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';

const Academicyear = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', status: 'Past' });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [academicyears, setacademicyears] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [schoolId, setSchoolId] = useState(null);

  // 1. Fetch current schoolId
  useEffect(() => {
    const fetchUserAndData = async () => {
      if (!auth.currentUser) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (userDoc.exists()) {
          const sId = userDoc.data().schoolId;
          setSchoolId(sId);
          fetchacademicyears(sId);
        }
      } catch (err) {
        toast.error("Error identifying school context");
      }
    };
    fetchUserAndData();
  }, []);

  // 2. Fetch list sorted by year
  const fetchacademicyears = async (sId) => {
    setFetchLoading(true);
    try {
      const q = query(
        collection(db, 'academicyears'), 
        where('schoolId', '==', sId),
        orderBy('name', 'desc')
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setacademicyears(data);
    } catch (error) {
      console.error(error);
      toast.error("Error fetching data");
    } finally {
      setFetchLoading(false);
    }
  };

  const handleOpen = () => {
    setFormData({ name: '', status: 'Past' });
    setEditMode(false);
    setEditId(null);
    setOpen(true);
  };

  const handleEdit = (item) => {
    setFormData({ name: item.name, status: item.status || 'Past' });
    setEditId(item.id);
    setEditMode(true);
    setOpen(true);
  };

  // 3. Handle Submit with "Single Active Year" Logic
  const handleSubmit = async () => {
    if (!formData.name.trim()) return toast.error("Year name (e.g. 2025-26) is required");
    setLoading(true);

    try {
      const batch = writeBatch(db);

      // If this one is being set to Active, set all others to Past
      if (formData.status === 'Active') {
        const otherActiveQuery = query(
          collection(db, 'academicyears'), 
          where('schoolId', '==', schoolId), 
          where('status', '==', 'Active')
        );
        const activeSnap = await getDocs(otherActiveQuery);
        activeSnap.forEach((d) => {
          if (d.id !== editId) batch.update(d.ref, { status: 'Past' });
        });
      }

      const payload = { 
        name: formData.name, 
        status: formData.status, 
        schoolId,
        updatedAt: new Date() 
      };

      if (editMode) {
        batch.update(doc(db, 'academicyears', editId), payload);
      } else {
        const newDocRef = doc(collection(db, 'academicyears'));
        batch.set(newDocRef, { ...payload, createdAt: new Date() });
      }

      await batch.commit();
      toast.success(editMode ? "Updated successfully" : "Added successfully");
      fetchacademicyears(schoolId);
      setOpen(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this year?")) return;
    try {
      await deleteDoc(doc(db, 'academicyears', id));
      toast.success("Deleted");
      fetchacademicyears(schoolId);
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  return (
    <Box p={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
           <Typography variant="h5" fontWeight="bold" color="primary">Academic Years</Typography>
           <Typography variant="caption" color="textSecondary">Manage sessions and active status</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen}>Add Year</Button>
      </Stack>

      <Card sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <TableContainer component={Paper} elevation={0}>
          <Table size="small">
            <TableHead sx={{ bgcolor: '#f8f9fa' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '80px' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Academic Year</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fetchLoading ? (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
              ) : academicyears.length === 0 ? (
                <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}>No Records Found</TableCell></TableRow>
              ) : (
                academicyears.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item, index) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell fontWeight="500">{item.name}</TableCell>
                    <TableCell>
                      <Chip 
                        label={item.status || 'Past'} 
                        size="small" 
                        color={item.status === 'Active' ? 'success' : 'default'}
                        variant={item.status === 'Active' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <IconButton color="primary" size="small" onClick={() => handleEdit(item)}><EditIcon fontSize="small" /></IconButton>
                        <IconButton color="error" size="small" onClick={() => handleDelete(item.id)}><DeleteIcon fontSize="small" /></IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={academicyears.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Card>

      {/* Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editMode ? 'Edit' : 'Add'} Session</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} pt={1}>
            <Grid item xs={12}>
              <TextField
                label="Academic Year (e.g. 2025-26)"
                name="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                fullWidth
                placeholder="2025-26"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Status"
                fullWidth
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
              >
                <MenuItem value="Active">Current Active</MenuItem>
                <MenuItem value="Past">Past Year</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpen(false)} color="inherit">Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading}
            sx={{ minWidth: 100 }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Academicyear;